import { Component, type ReactNode } from 'react'
import { Sentry } from '@/lib/sentry'
import { captureError } from '@/lib/analytics'

interface Props {
  children: ReactNode
  /** Optional fallback — defaults to full-page error screen */
  fallback?: ReactNode
}

interface State {
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } })
    captureError(error, { componentStack: info.componentStack })
  }

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="min-h-screen bg-[#e2e8f0] dark:bg-[#0f172a] flex flex-col items-center justify-center gap-6 p-8">
          <div className="bg-white dark:bg-[#1e293b] rounded-2xl shadow-lg p-8 max-w-sm w-full text-center space-y-4">
            <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto">
              <span className="text-2xl">!</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#0f172a] dark:text-white">Something went wrong</h2>
              <p className="text-sm text-[#64748b] dark:text-[#94a3b8] mt-1">
                The app hit an unexpected error. Your data is safe.
              </p>
            </div>
            <p className="text-xs text-[#94a3b8] font-mono bg-slate-100 dark:bg-slate-800 rounded-lg p-3 text-left break-all">
              {this.state.error.message}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-[#2563eb] text-white font-semibold py-3 rounded-xl active:opacity-80"
            >
              Reload App
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
